import React, { useCallback, useEffect, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useToast } from 'components/ui/use-toast';
import { appSettings, type AIFeatureSetting } from 'services/api/aiSettingsClient';
import { ManagedSettingsGroups, groupSettings } from './AISettings/ManagedSettingsList';

/** Support contact, retention, logging, media, and report settings. */
const AppSettingsSection: React.FC = () => {
  const { addToast } = useToast();
  const [features, setFeatures] = useState<AIFeatureSetting[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openFeature, setOpenFeature] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    appSettings.getOverview()
      .then((response) => { if (isMounted) setFeatures(response.data.features); })
      .catch(() => {
        if (isMounted) addToast({ title: 'Failed to load app settings', description: 'Please refresh and try again.', variant: 'destructive' });
      })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, [addToast]);

  const handleSaved = useCallback((next: AIFeatureSetting) => {
    setFeatures((current) => current?.map((feature) => (feature.key === next.key ? next : feature)) ?? current);
  }, []);

  return (
    <Card className="w-full max-w-none" maxWidth="none" data-testid="app-settings-section">
      <CardHeader>
        <CardTitle>App settings</CardTitle>
      </CardHeader>
      <CardContent className="items-stretch space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading app settings...</p>
        ) : features ? (
          <ManagedSettingsGroups
            groups={groupSettings(features)}
            client={appSettings}
            openFeature={openFeature}
            onToggle={(key) => setOpenFeature((current) => (current === key ? null : key))}
            onSaved={handleSaved}
          />
        ) : (
          <p className="text-sm text-muted-foreground">App settings are unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default AppSettingsSection;
